<template>
  <div class="draft-kpi-form">
    <KPIHeader
      :breadcrumbs-list="breadcrumbsList"
      :status="status"
      :version="version"
      :has_pending_changes="hasPendingChanges"
      :format="headerFormat"
      :name="displayName"
      :metric="displayMetric"
      :targets_type="headerTargetsType"
      :actual_calculation="actualCalculation"
      :less_is_favorable="lessIsFavorable"
    />

    <div v-if="showTabs" class="draft-kpi-form__tabs">
      <KPIFormMenu
        :has-pending-changes="hasPendingChanges"
        :value="activeTab"
        @onChange="onTabChange"
      />
    </div>

    <section
      v-if="isRevisionsTab"
      class="draft-kpi-form__revisions"
      aria-label="Revisions"
    ></section>

    <template v-else>
      <section class="draft-kpi-form__settings" aria-label="KPI settings">
        <div class="draft-kpi-form__settings-header">
          <div class="draft-kpi-form__settings-intro">
            <h2 class="draft-kpi-form__settings-title">KPI Settings</h2>
            <p class="draft-kpi-form__settings-subtitle">
              These settings determine the calculation behavior of your actuals and scores
            </p>
          </div>
          <div class="draft-kpi-form__settings-actions">
            <StandardButton
              v-if="isReadonly"
              label="Propose Changes"
              type="magenta"
              size="small"
              :loader-left="proposing"
              :disabled="actionsDisabled || proposing"
              @click="$emit('propose-changes')"
            />
            <template v-else>
              <StandardButton
                label="Discard"
                type="magenta"
                variant="outline"
                size="small"
                :disabled="actionsDisabled"
                @click="$emit('discard')"
              />
              <StandardButton
                label="Save Draft"
                type="magenta"
                variant="secondary"
                class="save-draft-button"
                size="small"
                :loader-left="saving"
                :disabled="actionsDisabled || saving"
                @click="$emit('save-draft')"
              />
              <StandardButton
                :label="publishLabel"
                type="magenta"
                size="small"
                :loader-left="publishing"
                :disabled="actionsDisabled || publishing || hasErrors"
                @click="$emit('publish')"
              />
            </template>
          </div>
        </div>

        <div class="draft-kpi-form__settings-grid">
          <div class="draft-kpi-form__row">
            <div class="draft-kpi-form__field">
              <ExTextInput
                label="Unit of Measure"
                :value="metric"
                :placeholder="unitOfMeasurePlaceholder"
                :disabled="isLocked('metric')"
                :error="Boolean(errors.metric)"
                :error-message="errors.metric || ''"
                hide-floating-label
                @input="onMetricInput"
              />
            </div>
            <div class="draft-kpi-form__field">
              <KPIFormatPicker
                :model-value="format"
                :disabled="isLocked('format')"
                @update:modelValue="onFormatChange"
              />
            </div>
            <div class="draft-kpi-form__field">
              <PrecisionPicker
                :decimal-precision-model="decimalPrecision"
                :disabled="isReadonly"
                @update:decimalPrecisionModel="onDecimalPrecisionChange"
              />
            </div>
          </div>

          <div class="draft-kpi-form__row">
            <div class="draft-kpi-form__field">
              <TargetsTypePicker
                :model-value="targetsType"
                label="Default Target Type"
                :disabled="isReadonly"
                @update:modelValue="onTargetsTypeChange"
              />
            </div>
            <div class="draft-kpi-form__field">
              <label class="draft-kpi-form__field-label">Default Aggregations</label>
              <ActualCalculationPicker
                :model-value="actualCalculation"
                :disabled="isReadonly"
                @update:modelValue="onActualCalculationChange"
              />
            </div>
            <div class="draft-kpi-form__field draft-kpi-form__field--favorability">
              <div class="draft-kpi-form__favorability">
                <FavorabilityToggle
                  :model-value="favorability"
                  :disabled="isLocked('favorability')"
                  @update:modelValue="onFavorabilityChange"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="draft-kpi-form__description" aria-label="KPI description">
        <h2 class="draft-kpi-form__description-title">Description</h2>
        <TiptapEditor
          :value="description"
          :editable="true"
          :readonly="isReadonly"
          :show-header="!isReadonly"
          :placeholder="descriptionPlaceholder"
          @input="onDescriptionInput"
          @update="onDescriptionUpdate"
        />
      </section>
    </template>
  </div>
</template>

<script setup>
import { computed } from "vue";
import KPIHeader from "@components/KPIHeader/KPIHeader.vue";
import KPIFormMenu from "@components/KPIFormMenu/KPIFormMenu.vue";
import KPIFormatPicker from "@components/KPIFormatPicker/KPIFormatPicker.vue";
import PrecisionPicker from "@components/PrecisionPicker/PrecisionPicker.vue";
import TargetsTypePicker from "@components/TargetsTypePicker/TargetsTypePicker.vue";
import ActualCalculationPicker from "@components/ActualCalculationPicker/ActualCalculationPicker.vue";
import FavorabilityToggle from "@components/FavorabilityToggle/FavorabilityToggle.vue";
import TiptapEditor from "@components/TiptapEditor/TiptapEditor.vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import {
  LESS_IS_FAVORABLE,
  MORE_IS_FAVORABLE,
} from "@components/constants/favorability";

const FORMAT_HEADER_LABELS = {
  CURRENCY: "Currency",
  PERCENT: "Percent",
  NUMBER: "Number",
};

const DEFAULT_METRIC_BY_FORMAT = {
  CURRENCY: "USD",
  PERCENT: "%",
  NUMBER: "",
};

const props = defineProps({
  breadcrumbsList: {
    type: Array,
    default: () => [
      { text: "KPIs Catalog", href: "#" },
      { text: "KPI", disabled: true },
    ],
  },
  /** draft: Draft KPI; current: read-only Active KPI; proposed: editing a pending draft. */
  mode: {
    type: String,
    default: "draft",
    validator: (v) => ["draft", "current", "proposed"].includes(v),
  },
  status: {
    type: String,
    default: "Draft",
  },
  hasPendingChanges: {
    type: Boolean,
    default: false,
  },
  activeTab: {
    type: Number,
    default: 0,
  },
  /** Fields shown read-only even while editing: "metric", "format", "favorability". */
  lockedFields: {
    type: Array,
    default: () => [],
  },
  /** Field-level validation messages, keyed by field (e.g. { metric: "Enter unit of measure" }). */
  errors: {
    type: Object,
    default: () => ({}),
  },
  version: {
    type: Number,
    default: 1,
  },
  name: {
    type: String,
    default: "New KPI",
  },
  metric: {
    type: String,
    default: "",
  },
  unitOfMeasurePlaceholder: {
    type: String,
    default: "Enter unit of measure",
  },
  descriptionPlaceholder: {
    type: String,
    default: "Describe this KPI...",
  },
  format: {
    type: String,
    default: "CURRENCY",
    validator: (v) => ["CURRENCY", "PERCENT", "NUMBER"].includes(v),
  },
  decimalPrecision: {
    type: [Number, String],
    default: 0,
  },
  targetsType: {
    type: String,
    default: "Fixed",
    validator: (v) => ["Fixed", "Prorated"].includes(v),
  },
  actualCalculation: {
    type: String,
    default: "Last",
    validator: (v) => ["Last", "Average"].includes(v),
  },
  lessIsFavorable: {
    type: Boolean,
    default: false,
  },
  description: {
    type: String,
    default: "",
  },
  saving: {
    type: Boolean,
    default: false,
  },
  publishing: {
    type: Boolean,
    default: false,
  },
  proposing: {
    type: Boolean,
    default: false,
  },
  actionsDisabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:format",
  "update:decimalPrecision",
  "update:targetsType",
  "update:actualCalculation",
  "update:lessIsFavorable",
  "update:description",
  "update:metric",
  "update:activeTab",
  "change",
  "discard",
  "save-draft",
  "publish",
  "propose-changes",
]);

const showTabs = computed(() => props.mode !== "draft");

const revisionsTabIndex = computed(() => (props.hasPendingChanges ? 2 : 1));

const isRevisionsTab = computed(
  () => showTabs.value && props.activeTab === revisionsTabIndex.value
);

const isReadonly = computed(() => props.mode === "current");

const hasErrors = computed(() => Object.values(props.errors).some(Boolean));

const publishLabel = computed(() => (props.mode === "proposed" ? "Publish Version" : "Publish"));

const displayName = computed(() => props.name || "New KPI");

const displayMetric = computed(() => {
  if (props.metric) {
    return props.metric;
  }
  return DEFAULT_METRIC_BY_FORMAT[props.format] ?? "";
});

const favorability = computed(() =>
  props.lessIsFavorable ? LESS_IS_FAVORABLE : MORE_IS_FAVORABLE
);

const headerFormat = computed(() => FORMAT_HEADER_LABELS[props.format] ?? "Currency");

const headerTargetsType = computed(() => props.targetsType || "Fixed");

function isLocked(field) {
  return isReadonly.value || props.lockedFields.includes(field);
}

function emitChange() {
  emit("change", {
    name: displayName.value,
    metric: displayMetric.value,
    format: props.format,
    decimalPrecision: props.decimalPrecision,
    targetsType: props.targetsType,
    actualCalculation: props.actualCalculation,
    lessIsFavorable: props.lessIsFavorable,
    description: props.description,
  });
}

function onTabChange(index) {
  emit("update:activeTab", index);
}

function onMetricInput(value) {
  emit("update:metric", value);
  emitChange();
}

function onFormatChange(value) {
  emit("update:format", value);
  emitChange();
}

function onDecimalPrecisionChange(value) {
  emit("update:decimalPrecision", value);
  emitChange();
}

function onTargetsTypeChange(value) {
  emit("update:targetsType", value);
  emitChange();
}

function onActualCalculationChange(value) {
  emit("update:actualCalculation", value);
  emitChange();
}

function onFavorabilityChange(value) {
  emit("update:lessIsFavorable", value === LESS_IS_FAVORABLE);
  emitChange();
}

function onDescriptionInput(value) {
  emit("update:description", value);
  emitChange();
}

function onDescriptionUpdate(value) {
  emit("update:description", value);
  emitChange();
}
</script>

<style scoped>
.save-draft-button {
  border-radius: var(--radius-md, 8px) !important;
  border: 1px solid #D6BBFB !important;
  background: #FFF !important;
  box-shadow: 0 1px 2px 0 #10182805 !important;
  color: #6941C6 !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  line-height: 20px !important;
}
.draft-kpi-form {
  display: flex;
  flex-direction: column;
  font-family: "Inter", sans-serif;
  border-radius: 12px;
  border: 1px solid #eaecf0;
  background: #fff;
  box-shadow: 0 1px 2px 0 #10182805;
}

.draft-kpi-form__settings {
  padding: 24px;
  border-top: 1px solid #eaecf0;
}

.draft-kpi-form__settings-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}

.draft-kpi-form__settings-title {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.draft-kpi-form__settings-subtitle {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
  max-width: 520px;
}

.draft-kpi-form__settings-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.draft-kpi-form__settings-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Row layout: Unit 30% | Format 15% | Precision 15% | (spare 40%) */
.draft-kpi-form__row {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1.5fr) minmax(0, 1.5fr) minmax(0, 4fr);
  column-gap: 16px;
  align-items: start;
}

.draft-kpi-form__field {
  min-width: 0;
}

.draft-kpi-form__field-label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.draft-kpi-form__field--favorability {
  grid-column: 3 / -1;
  display: flex;
  align-items: center;
}

.draft-kpi-form__favorability {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding-top: 26px;
}

.draft-kpi-form__tabs {
  padding: 0 24px;
}

.draft-kpi-form__revisions {
  min-height: 120px;
  border-top: 1px solid #eaecf0;
}

.draft-kpi-form__description {
  padding: 24px;
  border-top: 1px solid #eaecf0;
}

.draft-kpi-form__description-title {
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

/* Stretch child pickers to full column width */
.draft-kpi-form__field :deep(.kpi-format-picker),
.draft-kpi-form__field :deep(.precision-picker__section),
.draft-kpi-form__field :deep(.targets-type-picker),
.draft-kpi-form__field :deep(.dropdown-container) {
  width: 100%;
}

.draft-kpi-form__field :deep(.ex_text_input) {
  width: 100%;
}

.draft-kpi-form__field :deep(.ex_text_input .v-input--is-disabled input) {
  color: #475467;
  -webkit-text-fill-color: #475467;
}

.draft-kpi-form__field :deep(.ex_text_input .v-input--is-disabled .v-input__slot) {
  background-color: #f9fafb;
}

.draft-kpi-form__field :deep(.ex_text_input .v-input--is-disabled fieldset) {
  border: 1px solid #d0d5dd;
}

.draft-kpi-form__field :deep(.v-text-field) {
  margin-top: 0;
  padding-top: 0;
}

@media (max-width: 768px) {
  .draft-kpi-form__settings-header {
    flex-direction: column;
    align-items: stretch;
  }

  .draft-kpi-form__settings-actions {
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .draft-kpi-form__row {
    grid-template-columns: 1fr;
  }

  .draft-kpi-form__field--favorability {
    grid-column: auto;
  }

  .draft-kpi-form__favorability {
    padding-top: 0;
  }
}
</style>
